<script setup lang="ts">
import {
  getRegulatorDisclosureDetail,
  getRegulatorDisclosureList,
  type RegulatorDisclosureDetailData,
} from '~/api/regulator-disclosure'
import { getAliasResolve, getTraderDetail } from '~/server/detail'
import {
  buildDisclosureExchangeSlug,
  buildDisclosureRegulatorSlug,
  normalizeDisclosureSlug,
} from '~/utils/disclosure-route'

definePageMeta({
  name: 'disclosures-detail',
  layout: 'default',
  hideDefaultTopNav: true,
})

type RelatedBrand = {
  id: string
  alias?: string
  name: string
  englishFullName?: string
  logo: string
}

type MoreDisclosureItem = {
  disclosureId: string
  disclosureAlias: string
  disclosureType: string
  disclosureTypeColor: string
  content: string
  countryCode: string
  countryFlag: string
  countryName: string
  regulatorName: string
  disclosureDate: string
  traders: RelatedBrand[]
}

const DEFAULT_THEME_COLOR = '#2E5299'

const route = useRoute()
const { t, locale, localePath } = useAppI18n()
const { setBreadcrumb } = useLsBreadcrumb()
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()

/** 标准化路由参数，兼容 Nuxt 动态参数数组形态。 */
const normalizeRouteValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return String(value[0] || '').trim()
  }

  return String(value || '').trim()
}

/** 标准化接口文本字段，空值统一返回空字符串。 */
const normalizeText = (value: unknown) => {
  if (Array.isArray(value)) {
    return normalizeText(value[0])
  }

  return value === null || value === undefined ? '' : String(value).trim()
}

/** 提取接口返回的字符串数组，过滤空内容。 */
const normalizeStringList = (value: unknown) => {
  if (!Array.isArray(value)) return []
  return value.map((item) => normalizeText(item)).filter(Boolean)
}

const normalizeDisclosureAlias = (value: unknown) => normalizeText(value)

/** 格式化披露日期，兼容日期字符串和时间戳。 */
const formatDisclosureDate = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (typeof value === 'string') {
    const matched = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (matched) {
      return `${matched[1]}年${matched[2]}月${matched[3]}日`
    }
  }

  const timestamp = typeof value === 'number' ? value : Number(value)
  if (!Number.isNaN(timestamp) && Number.isFinite(timestamp)) {
    const date = new Date(timestamp)
    if (!Number.isNaN(date.getTime())) {
      const year = date.getUTCFullYear()
      const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
      const day = `${date.getUTCDate()}`.padStart(2, '0')
      return `${year}年${month}月${day}日`
    }
  }

  return normalizeText(value)
}

/** 格式化更多披露日期，使用 YYYY-MM-DD 展示。 */
const formatMoreDisclosureDate = (value: unknown) => {
  const disclosureDate = formatDisclosureDate(value)
  const matched = disclosureDate.match(/^(\d{4})年(\d{2})月(\d{2})日$/)
  return matched ? `${matched[1]}-${matched[2]}-${matched[3]}` : disclosureDate
}

/** 按换行拆分正文段落。 */
const splitParagraphs = (value: unknown) =>
  normalizeText(value)
    .split(/\r?\n+/)
    .map((item) => item.trim())
    .filter(Boolean)

/** 解析交易所展示名称，按接口字段优先级降级。 */
const resolveTraderName = (trader: Record<string, unknown> | null | undefined) =>
  normalizeText(trader?.showName) ||
  normalizeText(trader?.localShortName) ||
  normalizeText(trader?.englishShortName) ||
  normalizeText(trader?.shortName)

const resolveTraderEnglishFullName = (trader: Record<string, unknown> | null | undefined) =>
  normalizeText(trader?.englishFullName) || normalizeText(trader?.EnglishFullName)

const resolveTraderAlias = (trader: Record<string, unknown> | null | undefined) =>
  normalizeText(trader?.alias) || normalizeText(trader?.Alias)

/** 解析交易所 logo，兼容历史字段命名。 */
const resolveTraderLogo = (trader: Record<string, unknown> | null | undefined) =>
  normalizeText(trader?.logo) || normalizeText(trader?.ico) || normalizeText(trader?.icon)

/** 从 sortedLabels 中读取指定类型的标签内容。 */
const resolveSortedLabelContent = (value: unknown, labelType: number) => {
  if (!Array.isArray(value)) return ''

  const label = value.find((item) => Number((item as Record<string, unknown>)?.labelType) === labelType) as
    | Record<string, unknown>
    | undefined

  return normalizeText(label?.content)
}

/** 从类似 2027 / 2027year 的字段中提取四位年份，并格式化为本地化文案。 */
const formatOperatingYears = (value: unknown) => {
  const text = normalizeText(value)
  if (!text) return ''

  const matchedYear = text.match(/\b(\d{4})\b/) || text.match(/(\d{4})/)
  if (!matchedYear?.[1]) {
    return text
  }

  return t('84509', [matchedYear[1]])
}

const disclosureSlug = computed(() => normalizeRouteValue(route.params.slug))
const exchangeDetailAlias = computed(() => normalizeRouteValue(route.params.exchangeSlug))
const routeTraderCode = computed(
  () => normalizeRouteValue(route.query.code) || normalizeRouteValue(route.query.traderCode),
)

/** 生成交易所详情页本地化链接。 */
const exchangeDetailPageUrl = (slug: string) =>
  localePath({
    name: 'exchange-detail',
    params: { slug },
  })

/** 跳转交易商详情前缓存当前披露页为来源面包屑。 */
const setExchangeDetailBreadcrumb = (slug = exchangeDetailAlias.value || effectiveTraderCode.value) => {
  const breadcrumb = {
    labelKey: '84957',
    route: route.fullPath,
  }

  setBreadcrumb(slug, breadcrumb)

  if (exchangeDetailAlias.value && exchangeDetailAlias.value !== slug) {
    setBreadcrumb(exchangeDetailAlias.value, breadcrumb)
  }
}

const { data: disclosureDetailData } = await useAsyncData(
  () => `regulator-disclosure-detail-${disclosureSlug.value}`,
  async () => {
    if (!disclosureSlug.value) {
      return null
    }

    return await getRegulatorDisclosureDetail(disclosureSlug.value).catch(() => null)
  },
  {
    default: () => null as RegulatorDisclosureDetailData | null,
    watch: [disclosureSlug],
  },
)
// console.log(disclosureDetailData.value, 'disclosureDetailData')
const disclosureDetail = computed(() => disclosureDetailData.value || null)

const effectiveTraderCode = computed(() => {
  const traders = Array.isArray(disclosureDetail.value?.traders) ? disclosureDetail.value.traders : []
  const routeExchangeSlug = normalizeRouteValue(route.params.exchangeSlug)
  const routeMatchedTrader = traders.find((item) =>
    [resolveTraderAlias(item), resolveTraderEnglishFullName(item)].some(
      (value) => normalizeDisclosureSlug(value, '') === routeExchangeSlug,
    ),
  )

  return routeTraderCode.value || normalizeText(routeMatchedTrader?.code) || normalizeText(traders[0]?.code) || ''
})
// console.log(disclosureDetail.value, 'disclosureDetail')

const exchangeAliasForResolve = computed(() => normalizeRouteValue(route.params.exchangeSlug))
const { data: aliasResolveData } = await useAsyncData(
  () => `regulator-disclosure-alias-resolve-${exchangeAliasForResolve.value}`,
  async () => {
    if (!exchangeAliasForResolve.value || effectiveTraderCode.value) {
      return null
    }

    return await getAliasResolve({ alias: exchangeAliasForResolve.value }).catch(() => null)
  },
  {
    default: () => null,
    watch: [exchangeAliasForResolve, effectiveTraderCode],
  },
)

const resolvedTraderCode = computed(() => {
  const response = aliasResolveData.value as Record<string, any> | null
  const data = (response?.data || response) as Record<string, any> | null
  const items = Array.isArray(data?.items) ? data.items : []
  const resolvedItem = items.find((item) => normalizeText(item?.platform) === '2') || items[0]

  return normalizeText(resolvedItem?.traderCode || resolvedItem?.code) || exchangeAliasForResolve.value
})

const { data: traderDetailData } = await useAsyncData(
  () => `regulator-disclosure-trader-detail-${effectiveTraderCode.value}`,
  async () => {
    const traderCode = effectiveTraderCode.value || resolvedTraderCode.value
    if (!traderCode) {
      return null
    }

    return await getTraderDetail(traderCode).catch(() => null)
  },
  {
    default: () => null as any,
    watch: [effectiveTraderCode, resolvedTraderCode],
  },
)

const traderDisclosureSourceItems = computed<Record<string, unknown>[]>(() => {
  const source = traderDetailData.value?.regulationDisclosure
  const sourceItems = Array.isArray(source)
    ? source
    : Array.isArray(source?.disclosureList)
      ? source.disclosureList
      : Array.isArray(source?.items)
        ? source.items
        : []

  return sourceItems.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
})
const disclosureTraders = computed(() => {
  const detail = disclosureDetail.value

  return Array.isArray(detail?.traders) ? detail.traders : []
})

const matchedTrader = computed<Record<string, unknown> | null>(() => {
  const traders = disclosureTraders.value
  if (traders.length === 0) {
    return null
  }

  return (traders.find((item) => normalizeText(item?.code) === effectiveTraderCode.value) ||
    traders[0] ||
    null) as Record<string, unknown> | null
})

/** 当前披露关联主体列表。 */
const relatedBrands = computed<RelatedBrand[]>(() =>
  disclosureTraders.value
    .map((item) => ({
      id: normalizeText(item?.code) || resolveTraderName(item),
      alias: resolveTraderAlias(item),
      name: resolveTraderName(item),
      englishFullName: resolveTraderEnglishFullName(item),
      logo: resolveTraderLogo(item),
    }))
    .filter((item) => item.id && item.alias && item.name),
)

/** 当前页面主展示数据，集中做字段降级与默认值处理。 */
const currentDisclosure = computed(() => {
  const detail = disclosureDetail.value || {}
  const regInfo = detail.regInfo || {}
  const primaryTrader = matchedTrader.value
  const basicInfo = traderDetailData.value?.basicInfo || {}
  const isOriginal = Boolean(detail.isOriginal)

  return {
    id: disclosureSlug.value,
    title: normalizeText(detail.title),
    publishedAt: formatMoreDisclosureDate(detail.wiki_timestamp || detail.regTime),
    typeLabel: normalizeText(detail.tag?.tagName),
    typeColor: normalizeText(detail.tag?.color) || '#FCA700',
    exchangeName:
      resolveTraderName(primaryTrader) || normalizeText(regInfo.shortName) || normalizeText(regInfo.fullName),
    exchangeLogo: resolveTraderLogo(primaryTrader),
    annotation: normalizeText(primaryTrader?.annotation) || normalizeText(basicInfo.annotation),
    annotationColor: normalizeText(primaryTrader?.color) || normalizeText(basicInfo.color) || '#1f845a',
    operatingYears:
      resolveSortedLabelContent(primaryTrader?.sortedLabels, 101) ||
      resolveSortedLabelContent(basicInfo.sortedLabels, 101),
    isOriginal,
    contentParagraphs: !isOriginal ? splitParagraphs(detail.content) : [],
    originalText: isOriginal ? normalizeText(detail.content) : normalizeText(detail.originalContent),
    originalUrl: normalizeText(detail.originalUrl),
    proofFiles: normalizeStringList(detail.imgs),
    relatedBrands: relatedBrands.value,
    regulatorName: normalizeText(regInfo.fullName) || normalizeText(regInfo.shortName),
    enFullname: normalizeText(regInfo.enFullname),
    regulatorShortName: normalizeText(regInfo.shortName),
    countryName: normalizeText(regInfo.country),
    regulatorLogo: normalizeText(regInfo.logo),
    regulatorTime: formatOperatingYears(regInfo.time),
    themeColor: normalizeText(regInfo.themeColor) || DEFAULT_THEME_COLOR,
  }
})

/** 交易所面包屑名称与反馈详情保持一致：英文简称后拼接不重复的本地简称。 */
const exchangeBreadcrumbName = computed(() => {
  const basicInfo = traderDetailData.value?.basicInfo || {}
  const primaryTrader = matchedTrader.value
  const shortName =
    normalizeText(basicInfo.shortName) ||
    normalizeText(primaryTrader?.englishShortName) ||
    normalizeText(primaryTrader?.shortName)
  const localShortName = normalizeText(basicInfo.localShortName) || normalizeText(primaryTrader?.localShortName)

  if (shortName && localShortName && shortName !== localShortName) {
    return `${shortName} ${localShortName}`
  }

  return shortName || localShortName || currentDisclosure.value.exchangeName
})

/** 将交易所详情中的更多披露条目映射为右侧列表数据。 */
const mapMoreDisclosureItem = (item: Record<string, unknown>, fieldSource: 'item' | 'regInfo' = 'item') => {
  const tag = (item?.tag || item?.Tag) as Record<string, unknown> | undefined
  const regInfo = (item?.regInfo || item?.RegInfo) as Record<string, unknown> | undefined
  const countryText = normalizeText(fieldSource === 'regInfo' ? regInfo?.fullCountry : item?.fullCountry)
  const countryParts = countryText.split(/\s+/).filter(Boolean)
  const countryName = countryParts[0] || ''
  const regulatorName = countryParts.slice(1).join(' ')
  const traders = Array.isArray(item?.traders) ? item.traders : []
  const matchedDisclosureTrader =
    traders.find((trader) => normalizeText((trader as Record<string, unknown>)?.code) === effectiveTraderCode.value) ||
    traders[0] ||
    null

  return {
    disclosureId: normalizeText(item?.code || item?.Code),
    disclosureAlias: normalizeDisclosureAlias(item?.alias || item?.Alias || item?.disclosureAlias || item?.slug),
    disclosureType:
      normalizeText(item?.categoryName || item?.CategoryName) ||
      normalizeText(tag?.tagName || tag?.TagName) ||
      normalizeText(item?.category || item?.Category),
    disclosureTypeColor:
      normalizeText(item?.categoryColor || item?.CategoryColor) || normalizeText(tag?.color || tag?.Color) || '#E2483D',
    content: normalizeText(item?.title || item?.Title || item?.content || item?.Content),
    countryCode: normalizeText(regInfo?.countryCode || regInfo?.CountryCode),
    countryFlag: normalizeText(fieldSource === 'regInfo' ? regInfo?.flag : item?.flag || item?.Flag),
    countryName,
    regulatorName,
    disclosureDate: formatMoreDisclosureDate(item?.regTime || item?.RegTime),
    traders: matchedDisclosureTrader
      ? [
          {
            id: normalizeText((matchedDisclosureTrader as Record<string, unknown>)?.code),
            name: resolveTraderName((matchedDisclosureTrader as Record<string, unknown>) || {}),
            englishFullName: resolveTraderEnglishFullName((matchedDisclosureTrader as Record<string, unknown>) || {}),
            logo: resolveTraderLogo((matchedDisclosureTrader as Record<string, unknown>) || {}),
          },
        ].filter((trader) => trader.id && trader.name)
      : [],
  }
}

const allMoreDisclosures = computed<MoreDisclosureItem[]>(() => {
  const source = traderDetailData.value?.regulationDisclosure
  const sourceItems = Array.isArray(source)
    ? source
    : Array.isArray(source?.disclosureList)
      ? source.disclosureList
      : Array.isArray(source?.items)
        ? source.items
        : []

  return sourceItems
    .map((item: Record<string, unknown>) => mapMoreDisclosureItem(item))
    .filter((item) => item.disclosureAlias && item.disclosureAlias !== disclosureSlug.value)
})

const fallbackRegulatorCode = computed(
  () =>
    normalizeText(disclosureDetail.value?.regulatorCode) ||
    normalizeText((disclosureDetail.value?.regInfo as Record<string, unknown> | undefined)?.code),
)

// console.log(fallbackRegulatorCode.value, 'fallbackRegulatorCode')
const { data: fallbackDisclosureItems } = await useAsyncData(
  () => `regulator-disclosure-list-fallback-${fallbackRegulatorCode.value}`,
  async () => {
    if (allMoreDisclosures.value.length > 0 || !fallbackRegulatorCode.value) {
      return []
    }

    return await getRegulatorDisclosureList(fallbackRegulatorCode.value).catch((error) => {
      return []
    })
  },
  {
    default: () => [] as Record<string, unknown>[],
    watch: [fallbackRegulatorCode, allMoreDisclosures],
  },
)

const fallbackMoreDisclosures = computed(() =>
  fallbackDisclosureItems.value
    .map((item) => mapMoreDisclosureItem(item, 'regInfo'))
    .filter((item) => item.disclosureAlias && item.disclosureAlias !== disclosureSlug.value),
)

watch(
  [fallbackDisclosureItems, fallbackMoreDisclosures],
  ([items, mappedItems]) => {
    if (import.meta.server) return
  },
  { immediate: true },
)

onMounted(() => {})

const combinedMoreDisclosures = computed(() =>
  allMoreDisclosures.value.length > 0 ? allMoreDisclosures.value : fallbackMoreDisclosures.value,
)
const moreDisclosures = computed(() => combinedMoreDisclosures.value.slice(0, 3))
const showMoreDisclosureDialogTrigger = computed(() => combinedMoreDisclosures.value.length > 3)

watch(
  [allMoreDisclosures, combinedMoreDisclosures, moreDisclosures],
  ([detailItems, combinedItems, visibleItems]) => {
    if (import.meta.server) return
  },
  { immediate: true },
)
const moreDisclosureDialogVisible = ref(false)
const feedbackDialogVisible = ref(false)

watch([moreDisclosureDialogVisible, feedbackDialogVisible], ([isMoreDisclosureOpen, isFeedbackOpen]) => {
  if (isMoreDisclosureOpen || isFeedbackOpen) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})

const exchangeLogo = computed(() => {
  const basicInfo = traderDetailData.value?.basicInfo
  return normalizeText(basicInfo?.ico) || ''
})

const exchangeName = computed(() => {
  const basicInfo = traderDetailData.value?.basicInfo
  const shortName = normalizeText(basicInfo?.shortName)
  const localShortName = normalizeText(basicInfo?.localShortName)
  if (shortName) {
    if (localShortName && localShortName !== shortName) {
      return `${shortName} · ${localShortName}`
    }
    return shortName
  }
  return effectiveTraderCode.value || ''
})
const exchangeAlias = computed(() => resolveTraderAlias(matchedTrader.value))
const originalTextExpanded = ref(false)
const originalTextRef = ref<HTMLElement | null>(null)
const originalTextOverflow = ref(false)

/** 检测原文是否超过折叠高度。 */
const updateOriginalTextOverflow = async () => {
  await nextTick()
  const element = originalTextRef.value
  originalTextOverflow.value = Boolean(element && element.scrollHeight > element.clientHeight + 1)
}

onMounted(() => {
  updateOriginalTextOverflow()
  window.addEventListener('resize', updateOriginalTextOverflow)
})

onBeforeUnmount(() => {
  unlockPageScroll()
  window.removeEventListener('resize', updateOriginalTextOverflow)
})

watch(
  () => currentDisclosure.value.originalText,
  () => {
    originalTextExpanded.value = false
    updateOriginalTextOverflow()
  },
)

const openMoreDisclosureDialog = () => {
  if (showMoreDisclosureDialogTrigger.value) {
    moreDisclosureDialogVisible.value = true
  }
}

const closeMoreDisclosureDialog = () => {
  moreDisclosureDialogVisible.value = false
}

watchEffect(() => {
  if (!currentDisclosure.value.title || (!effectiveTraderCode.value && !exchangeDetailAlias.value)) {
    return
  }

  setExchangeDetailBreadcrumb()
})

/** 面包屑配置，第三级使用披露详情的国际化文案。 */
const breadcrumbItems = computed(() => {
  const items: Array<{
    label: string
    route?: string | Record<string, unknown>
    color?: string
  }> = [
    {
      label: t('82520'),
      route: '/',
      color: 'rgba(255,255,255,0.6)',
    },
  ]

  if (exchangeDetailAlias.value && exchangeBreadcrumbName.value) {
    items.push({
      label: exchangeBreadcrumbName.value,
      route: {
        name: 'exchange-detail',
        params: {
          slug: exchangeDetailAlias.value,
        },
      },
      color: 'rgba(255,255,255,0.6)',
    })
  }

  items.push({
    label: t('84957'),
    color: '#ffffff',
  })

  return items
})

/** 生成更多披露详情页本地化链接。 */
const disclosurePageUrl = (item: MoreDisclosureItem) =>
  localePath({
    name: 'disclosures-detail',
    params: {
      regulatorSlug: buildDisclosureRegulatorSlug(
        item.countryCode,
        item.regulatorName || currentDisclosure.value.regulatorName,
        item.countryName || currentDisclosure.value.countryName,
      ),
      exchangeSlug: buildDisclosureExchangeSlug(
        item.traders[0]?.englishFullName,
        normalizeRouteValue(route.params.exchangeSlug),
      ),
      slug: item.disclosureAlias,
    },
  })

const disclosureCertificatePageUrl = computed(() =>
  localePath({
    path: `/exchanges/${normalizeRouteValue(route.params.exchangeSlug)}/licenses`,
    query: {
      from: 'disclosures-detail',
      slug: disclosureSlug.value,
    },
  }),
)

useHead(() => ({
  title: t('84360', [currentDisclosure.value.regulatorName]),
  meta: [
    {
      name: 'description',
      content: t('84360', [currentDisclosure.value.regulatorName]),
    },
  ],
}))
</script>

<template>
  <HeaderWikiBitPcTopNav :always-expand-search="false" :special="true" />
  <main class="bg-[#ffffff] text-[#292a2e]">
    <section
      class="regulator-disclosure-hero pt-[94px] pb-[154px]"
      :style="{ '--hero-theme-color': currentDisclosure.themeColor }"
    >
      <div class="mx-auto w-[min(1200px,calc(100vw-48px))]">
        <CommonBreadcrumb :items="breadcrumbItems" class="disclosure-breadcrumb" />

        <div class="flex items-center justify-between pt-[50px]">
          <div class="flex items-start gap-[32px]">
            <div
              class="flex h-[94px] w-[166px] shrink-0 items-center justify-center rounded-[8px] bg-[#ffffff] px-[14px] py-[12px]"
            >
              <CommonImage
                v-if="currentDisclosure.regulatorLogo"
                :src="currentDisclosure.regulatorLogo"
                :alt="currentDisclosure.regulatorName"
                class="h-full w-full"
                object-fit="contain"
                loading="eager"
              />
            </div>

            <div class="flex min-w-0 flex-col gap-[16px]">
              <h1 class="line-clamp-2 overflow-hidden text-[36px] leading-[50px] font-[600] text-[#ffffff]">
                {{ currentDisclosure.regulatorName }}
                <span v-if="currentDisclosure.regulatorShortName"> {{ currentDisclosure.regulatorShortName }}</span>
              </h1>
              <span v-if="locale === 'zh-cn' || locale === 'zh'" class="text-[16px] leading-[24px] text-[#fff]">
                {{ currentDisclosure.enFullname }}
              </span>
              <span
                v-else-if="currentDisclosure.regulatorTime"
                class="w-fit rounded-[2px] border-[0.5px] border-[rgba(5,21,36,0.06)] bg-[rgba(255,255,255,0.14)] px-[8px] py-[2px] text-[14px] leading-[22px] font-[400] text-[#ffffff]"
              >
                {{ currentDisclosure.regulatorTime }}
              </span>
            </div>
          </div>
          <div v-if="locale === 'zh-cn' || locale === 'zh'" class="flex flex-col items-end">
            <div class="items-right text-[14px] leading-[22px] text-[rgba(255,255,255,0.6)]">{{ t('84672') }}</div>
            <div class="items-right text-[14px] leading-[22px] font-[500] text-[#fff]">
              {{ currentDisclosure.regulatorTime }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="relative z-[1] mx-auto mt-[-30px] w-[min(1200px,calc(100vw-48px))] pb-[80px]">
      <div class="disclosure-main-grid grid grid-cols-[minmax(0,790px)_minmax(0,384px)] items-start gap-[26px]">
        <div class="flex min-w-0 flex-col gap-[40px]">
          <section
            class="flex items-center justify-between rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] py-[32px] shadow-[0_12px_40px_rgba(17,45,115,0.04)]"
          >
            <div class="flex min-w-0 items-center gap-[16px]">
              <div
                class="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-[#f4f7ff]"
              >
                <CommonImage
                  v-if="currentDisclosure.exchangeLogo"
                  :src="currentDisclosure.exchangeLogo"
                  :alt="currentDisclosure.exchangeName"
                  class="size-full"
                  object-fit="cover"
                  loading="eager"
                />
              </div>

              <div class="flex min-w-0 flex-col gap-[8px]">
                <div class="truncate text-[24px] leading-[32px] font-[600] text-[#292a2e]">
                  {{ currentDisclosure.exchangeName || '--' }}
                </div>
                <div
                  v-if="currentDisclosure.annotation || currentDisclosure.operatingYears"
                  class="flex flex-wrap items-center gap-[8px] text-[12px] leading-[18px] font-[400]"
                >
                  <span v-if="currentDisclosure.annotation" :style="{ color: currentDisclosure.annotationColor }">
                    {{ currentDisclosure.annotation }}
                  </span>
                  <span
                    v-if="currentDisclosure.annotation && currentDisclosure.operatingYears"
                    class="h-[14px] w-[1px] bg-[rgba(5,21,36,0.12)]"
                  ></span>
                  <span v-if="currentDisclosure.operatingYears" class="text-[#7d818a]">
                    {{ currentDisclosure.operatingYears }}
                  </span>
                </div>
              </div>
            </div>

            <span
              v-if="currentDisclosure.typeLabel"
              class="shrink-0 text-[18px] leading-[26px] font-[600]"
              :style="{ color: currentDisclosure.typeColor }"
            >
              {{ currentDisclosure.typeLabel }}
            </span>
          </section>

          <article class="flex min-w-0 flex-col gap-[64px]">
            <section class="flex flex-col gap-[64px]">
              <div class="flex flex-col">
                <div class="flex flex-col gap-[16px]">
                  <h2 class="text-[28px] leading-[36px] font-[600] text-[#292a2e]">
                    {{ currentDisclosure.title || '暂无披露标题' }}
                  </h2>
                  <p
                    v-if="currentDisclosure.publishedAt"
                    class="text-[12px] leading-[18px] font-[400] text-[#7d818a] opacity-70"
                  >
                    {{ t('84511', [currentDisclosure.publishedAt]) }}
                  </p>
                </div>

                <div v-if="currentDisclosure.contentParagraphs.length > 0" class="mt-[40px] flex flex-col">
                  <p
                    v-for="paragraph in currentDisclosure.contentParagraphs"
                    :key="paragraph"
                    class="text-[16px] leading-[28px] font-[400] text-[#505258]"
                  >
                    {{ paragraph }}
                  </p>
                </div>
              </div>

              <section
                v-if="
                  currentDisclosure.originalText ||
                  currentDisclosure.originalUrl ||
                  currentDisclosure.proofFiles.length > 0
                "
                class="flex flex-col gap-[24px]"
              >
                <h3 class="text-[24px] leading-[32px] font-[600] text-[#292a2e]">{{ t('84510') }}</h3>
                <p
                  v-if="currentDisclosure.originalText"
                  ref="originalTextRef"
                  class="text-[16px] leading-[28px] font-[400] text-[#505258]"
                  :class="{ 'original-text-collapsed': !originalTextExpanded }"
                >
                  {{ currentDisclosure.originalText }}
                </p>
                <button
                  v-if="currentDisclosure.originalText && originalTextOverflow && !originalTextExpanded"
                  type="button"
                  class="mx-auto inline-flex w-fit cursor-pointer items-center gap-[4px] text-[14px] leading-[22px] font-[400] text-[#0d3f63] transition-opacity hover:opacity-80"
                  @click="originalTextExpanded = true"
                >
                  {{ t('84516') }}
                  <span class="original-text-more-icon"></span>
                </button>

                <div class="flex flex-wrap items-center gap-x-[32px] gap-y-[16px]">
                  <div v-if="currentDisclosure.originalUrl" class="flex items-center gap-[4px]">
                    <span class="text-[16px] leading-[24px] font-[400] text-[#505258]">{{ t('84513') }}</span>
                    <NuxtLink
                      :to="currentDisclosure.originalUrl"
                      external
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex cursor-pointer items-center gap-[4px] rounded-[4px] border-[0.5px] border-[rgba(13,63,99,0.1)] bg-[rgba(13,63,99,0.04)] px-[12px] py-[4px] text-[14px] leading-[22px] font-[400] text-[#0d3f63] no-underline transition-opacity hover:opacity-80"
                    >
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/06/26/7d1bc55d-4442-4b17-849e-3518aeff99be.png"
                        alt=""
                        class="size-[16px]"
                        style="width: 16px; height: 16px"
                        object-fit="cover"
                        loading="eager"
                      />
                      {{ t('84512') }}
                    </NuxtLink>
                  </div>

                  <div v-if="currentDisclosure.proofFiles.length > 0" class="flex flex-wrap items-center gap-[8px]">
                    <span class="text-[16px] leading-[24px] font-[400] text-[#505258]">{{ t('83583') }}</span>
                    <NuxtLink
                      :to="disclosureCertificatePageUrl"
                      target="_blank"
                      class="inline-flex cursor-pointer items-center gap-[4px] rounded-[4px] border-[0.5px] border-[rgba(13,63,99,0.1)] bg-[rgba(13,63,99,0.04)] px-[12px] py-[4px] text-[14px] leading-[22px] font-[400] text-[#0d3f63] no-underline transition-opacity hover:opacity-80"
                    >
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/06/26/dcc67bcc-c990-4ff7-8e20-fded036c755b.png"
                        alt=""
                        class="size-[16px]"
                        style="width: 16px; height: 16px"
                        object-fit="cover"
                        loading="eager"
                      />
                      {{ t('84515') }} {{ currentDisclosure.proofFiles.length }}
                    </NuxtLink>
                  </div>
                </div>
              </section>

              <section v-if="currentDisclosure.relatedBrands.length > 0" class="flex flex-col gap-[24px]">
                <h3 class="text-[24px] leading-[32px] font-[600] text-[#292a2e]">{{ t('84520') }}</h3>
                <div class="flex flex-wrap gap-[12px]">
                  <NuxtLink
                    v-for="brand in currentDisclosure.relatedBrands"
                    :key="brand.id"
                    :to="exchangeDetailPageUrl(brand.alias)"
                    target="_blank"
                    class="inline-flex cursor-pointer items-center gap-[6px] rounded-[4px] border border-[rgba(11,18,40,0.1)] bg-[#ffffff] px-[10px] py-[6px] no-underline transition-colors hover:border-[#112d73]"
                    @click="setExchangeDetailBreadcrumb(brand.alias)"
                  >
                    <CommonImage
                      v-if="brand.logo"
                      :src="brand.logo"
                      :alt="brand.name"
                      class="size-[16px] rounded-[2px]"
                      style="width: 16px; height: 16px"
                      object-fit="cover"
                      loading="eager"
                    />
                    <span class="text-[12px] leading-[18px] font-[400] text-[#505258]">{{ brand.name }}</span>
                  </NuxtLink>
                </div>
              </section>
            </section>
          </article>
        </div>

        <aside class="flex min-w-0 flex-col gap-[32px]">
          <section
            v-if="moreDisclosures.length > 0"
            class="rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] py-[24px]"
          >
            <div class="mb-[20px] flex items-center justify-between gap-[12px]">
              <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('84521') }}</h3>
            </div>

            <div class="flex flex-col">
              <NuxtLink
                v-for="(item, index) in moreDisclosures"
                :key="item.disclosureId"
                :to="disclosurePageUrl(item)"
                target="_blank"
                class="group flex flex-col gap-[12px] py-[16px] no-underline hover:bg-[#f8f8f8]"
                :class="index < moreDisclosures.length - 1 ? 'border-b-[0.5px] border-[rgba(5,21,36,0.06)]' : ''"
              >
                <span class="text-[14px] leading-[22px] font-[600]" :style="{ color: item.disclosureTypeColor }">
                  {{ item.disclosureType }}
                </span>
                <p class="text-[16px] leading-[24px] font-[500] text-[#292a2e] transition-colors">
                  {{ item.content }}
                </p>
                <div
                  class="flex flex-wrap items-center gap-x-[8px] gap-y-[6px] text-[12px] leading-[18px] font-[400] text-[#7d818a]"
                >
                  <span
                    v-for="trader in item.traders.slice(0, 1)"
                    :key="trader.id"
                    class="inline-flex items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] px-[8px] py-[2px]"
                  >
                    <CommonImage
                      v-if="trader.logo"
                      :src="trader.logo"
                      alt=""
                      aria-hidden="true"
                      class="size-[14px] rounded-[2px]"
                      style="width: 14px; height: 14px"
                      object-fit="cover"
                      loading="eager"
                    />
                    <span>{{ trader.name }}</span>
                  </span>
                  <span class="inline-flex items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] px-[8px] py-[2px]">
                    <CommonImage
                      v-if="item.countryFlag"
                      :src="item.countryFlag"
                      alt=""
                      aria-hidden="true"
                      class="h-[12px] w-[18px] rounded-[2px]"
                      style="width: 18px; height: 12px"
                      object-fit="cover"
                      loading="eager"
                    />
                    <span>{{ [item.countryName, item.regulatorName].filter(Boolean).join(' ') || '--' }}</span>
                  </span>
                  <span>{{ item.disclosureDate }}</span>
                </div>
              </NuxtLink>
            </div>

            <div v-if="showMoreDisclosureDialogTrigger" class="pt-[16px] text-center">
              <button
                type="button"
                class="inline-flex cursor-pointer items-center justify-center text-[13px] leading-[20px] font-[400] text-[#0d3f63] transition-opacity hover:opacity-80"
                @click="openMoreDisclosureDialog"
              >
                {{ t('84516') }}
              </button>
            </div>
          </section>

          <section
            class="flex flex-col items-center gap-[20px] rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-[#ffffff] px-[24px] py-[24px] text-center"
          >
            <div class="flex h-[118px] w-[246px] items-center justify-center overflow-hidden">
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/06/16/275862e8-8144-4e87-9d0e-3961edcd2f72.png"
                alt=""
                aria-hidden="true"
                class="h-[118px] w-[246px] object-contain"
                loading="eager"
              />
            </div>

            <div class="flex flex-col gap-[8px]">
              <h3 class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('84517') }}</h3>
              <p class="text-[16px] leading-[24px] font-[400] text-[#505258]">
                {{ t('84518', [currentDisclosure.exchangeName || exchangeName]) }}
              </p>
            </div>

            <button
              type="button"
              class="h-[46px] min-w-[184px] cursor-pointer rounded-[100px] bg-[#112d73] px-[4px] py-[12px] text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#ffffff] transition-colors hover:bg-[#173c91]"
              @click="feedbackDialogVisible = true"
            >
              {{ t('84519') }}
            </button>
          </section>
        </aside>
      </div>
    </section>

    <ExchangeDetailExchangeFeedbackDialog
      :show="feedbackDialogVisible"
      @close="feedbackDialogVisible = false"
      @example="feedbackDialogVisible = false"
      :allow-exchange-search="false"
      :trader-code="effectiveTraderCode"
      :exchange-slug="exchangeAlias"
      :feedback-logo="exchangeLogo"
      :feedback-name="exchangeName"
    />

    <Teleport to="body">
      <div
        v-if="moreDisclosureDialogVisible"
        class="fixed inset-[0] z-[1200] flex items-center justify-center bg-[rgba(11,18,40,0.36)] p-[24px]"
        @click.self="closeMoreDisclosureDialog"
      >
        <section
          class="w-[760px] max-w-[calc(100vw-48px)] overflow-hidden rounded-[28px] bg-[#ffffff] shadow-[0_24px_80px_rgba(8,19,62,0.18)]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="more-disclosure-dialog-title"
        >
          <header
            class="flex items-center justify-between gap-[8px] px-[32px] pt-[32px] pb-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)]"
          >
            <h2 id="more-disclosure-dialog-title" class="m-[0] text-[20px] leading-[28px] font-[600] text-[#292a2e]">
              {{ t('84521') }}
            </h2>
            <button
              type="button"
              class="relative inline-flex size-[32px] cursor-pointer items-center justify-center rounded-[999px] border-[0] bg-[#f3f5f8] p-[0] transition-colors hover:bg-[#e9edf3]"
              aria-label="关闭更多披露弹窗"
              @click="closeMoreDisclosureDialog"
            >
              <span class="more-disclosure-dialog-close-mark relative block size-[12px]"></span>
            </button>
          </header>

          <div class="max-h-[min(640px,calc(100vh-160px))] overflow-y-auto px-[32px] pt-[20px] pb-[32px]">
            <NuxtLink
              v-for="(item, index) in combinedMoreDisclosures"
              :key="`${item.disclosureId}-${index}`"
              :to="disclosurePageUrl(item)"
              target="_blank"
              class="flex flex-col gap-[12px] py-[16px] text-[inherit] no-underline shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] hover:bg-[#f8f8f8]"
              :class="{ 'shadow-none': index === combinedMoreDisclosures.length - 1 }"
            >
              <span class="text-[14px] leading-[22px] font-[600]" :style="{ color: item.disclosureTypeColor }">
                {{ item.disclosureType }}
              </span>
              <p class="text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                {{ item.content }}
              </p>
              <div
                class="flex flex-wrap items-center gap-x-[8px] gap-y-[6px] text-[12px] leading-[18px] font-[400] text-[#7d818a]"
              >
                <span
                  v-for="trader in item.traders.slice(0, 1)"
                  :key="trader.id"
                  class="inline-flex items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] px-[8px] py-[2px]"
                >
                  <CommonImage
                    v-if="trader.logo"
                    :src="trader.logo"
                    alt=""
                    aria-hidden="true"
                    class="size-[14px] rounded-[2px]"
                    style="width: 14px; height: 14px"
                    object-fit="cover"
                    loading="eager"
                  />
                  <span>{{ trader.name }}</span>
                </span>
                <span class="inline-flex items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] px-[8px] py-[2px]">
                  <CommonImage
                    v-if="item.countryFlag"
                    :src="item.countryFlag"
                    alt=""
                    aria-hidden="true"
                    class="h-[12px] w-[18px] rounded-[2px]"
                    style="width: 18px; height: 12px"
                    object-fit="cover"
                    loading="eager"
                  />
                  <span>{{ [item.countryName, item.regulatorName].filter(Boolean).join(' ') || '--' }}</span>
                </span>
                <span>{{ item.disclosureDate }}</span>
              </div>
            </NuxtLink>
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>

<style scoped lang="less">
.regulator-disclosure-hero {
  height: 360px;
  position: relative;
  overflow: hidden;
  background: url('https://resources1.interface003.com/web30/2026/06/17/db055a7c-39b1-462d-9636-c40d609cdb4d.png');
  background-color: #2e5299;
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 1920px 360px;
}

.disclosure-breadcrumb {
  padding: 0 !important;
}

.disclosure-breadcrumb:deep(nav) {
  flex-wrap: wrap;
  row-gap: 6px;
}

.original-text-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
}

.original-text-more-icon {
  width: 6px;
  height: 6px;
  border-right: 1px solid currentColor;
  border-bottom: 1px solid currentColor;
  transform: rotate(45deg) translateY(-1px);
}

.more-disclosure-dialog-close-mark::before,
.more-disclosure-dialog-close-mark::after {
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  width: 12px;
  height: 1.5px;
  border-radius: 999px;
  background: #7d818a;
  content: '';
  transform-origin: center;
}

.more-disclosure-dialog-close-mark::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.more-disclosure-dialog-close-mark::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .disclosure-main-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media screen and (max-width: 799px) {
  .regulator-disclosure-hero {
    padding-bottom: 128px;
  }

  .regulator-disclosure-hero::before,
  .regulator-disclosure-hero::after {
    opacity: 0.4;
  }

  .disclosure-main-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
